/**
 * BestOddsActiveFilterChips - Removable chips for all active Best Odds filters
 * (v1 + v1.1) plus Clear all.
 *
 * Multi-select groups (bookmakers / championships / markets) collapse to a
 * single summary chip when more than MAX_VISIBLE_GROUP_CHIPS are selected.
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useBestOdds } from '../context/BestOddsContext';
import { useTranslation } from '../../i18n/hooks';
import { getBookmakerName, getMarginTypeName } from '../../surebets/utils/surebetUtils';
import { normalizeSearchText, TIME_WINDOW_VALUES } from '../utils/bestOddsFilters';
import { buildGroupChips } from '../utils/bestOddsActiveChips';
import './BestOddsActiveFilterChips.css';

const TIME_WINDOW_LABEL_KEYS = {
  today: 'bestOdds.filters.timeWindowToday',
  next24h: 'bestOdds.filters.timeWindowNext24h',
  next48h: 'bestOdds.filters.timeWindowNext48h'
};

const BestOddsActiveFilterChips = ({ className = '' }) => {
  const { t, language } = useTranslation();
  const {
    filters,
    hasActiveFilters,
    setMinSpread,
    setMaxOdds,
    setSearchQuery,
    setSelectedBookmakers,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetFilters
  } = useBestOdds();

  const chips = useMemo(() => {
    const next = [];
    const minSpread = Number(filters?.minSpread);
    const maxOdds = Number(filters?.maxOdds);
    const searchQuery = filters?.searchQuery || '';
    const timeWindow = filters?.timeWindow;
    const selectedBookmakers = filters?.selectedBookmakers;
    const selectedChampionships = filters?.selectedChampionships;
    const selectedMarketKeys = filters?.selectedMarketKeys;

    if (Number.isFinite(minSpread) && minSpread > 0) {
      next.push({
        id: 'spread',
        type: 'spread',
        label: t('bestOdds.filters.spreadChip', { value: minSpread })
      });
    }

    if (Number.isFinite(maxOdds) && maxOdds > 0) {
      next.push({
        id: 'maxOdds',
        type: 'maxOdds',
        label: t('bestOdds.filters.maxOddsChip', { value: maxOdds })
      });
    }

    if (normalizeSearchText(searchQuery)) {
      next.push({
        id: 'search',
        type: 'search',
        label: `“${searchQuery.trim()}”`
      });
    }

    if (timeWindow && timeWindow !== 'all' && TIME_WINDOW_VALUES.includes(timeWindow)) {
      const windowLabel = t(TIME_WINDOW_LABEL_KEYS[timeWindow] || 'bestOdds.filters.timeWindowLabel');
      next.push({
        id: 'timeWindow',
        type: 'timeWindow',
        label: `${t('bestOdds.filters.timeWindowLabel')}: ${windowLabel}`
      });
    }

    next.push(...buildGroupChips({
      selected: selectedBookmakers,
      noneId: 'bookmakers-none',
      noneType: 'bookmakers-none',
      noneLabel: `${t('bestOdds.filters.bookmakersLabel')}: 0`,
      itemType: 'bookmaker',
      idPrefix: 'bookmaker-',
      getItemId: (id) => id,
      getItemLabel: (id) => getBookmakerName(id),
      summaryId: 'bookmakers-summary',
      summaryType: 'bookmakers-summary',
      summaryLabel: `${t('bestOdds.filters.bookmakersLabel')}: ${selectedBookmakers?.length || 0}`
    }));

    next.push(...buildGroupChips({
      selected: selectedChampionships,
      noneId: 'championships-none',
      noneType: 'championships-none',
      noneLabel: t('bestOdds.filters.championshipsNone'),
      itemType: 'championship',
      idPrefix: 'championship-',
      getItemId: (name) => name,
      getItemLabel: (name) => name,
      summaryId: 'championships-summary',
      summaryType: 'championships-summary',
      summaryLabel: `${t('bestOdds.filters.championshipsLabel')}: ${selectedChampionships?.length || 0}`
    }));

    next.push(...buildGroupChips({
      selected: selectedMarketKeys,
      noneId: 'markets-none',
      noneType: 'markets-none',
      noneLabel: t('bestOdds.filters.marketsNone'),
      itemType: 'market',
      idPrefix: 'market-',
      getItemId: (key) => key,
      getItemLabel: (key) => getMarginTypeName(key, language),
      summaryId: 'markets-summary',
      summaryType: 'markets-summary',
      summaryLabel: `${t('bestOdds.filters.marketsLabel')}: ${selectedMarketKeys?.length || 0}`
    }));

    return next;
  }, [filters, language, t]);

  const handleRemoveChip = useCallback((chip) => {
    if (chip.type === 'spread') {
      setMinSpread(0);
      return;
    }

    if (chip.type === 'maxOdds') {
      setMaxOdds(0);
      return;
    }

    if (chip.type === 'search') {
      setSearchQuery('');
      return;
    }

    if (chip.type === 'timeWindow') {
      setTimeWindow('all');
      return;
    }

    if (chip.type === 'bookmakers-none' || chip.type === 'bookmakers-summary') {
      setSelectedBookmakers(null);
      return;
    }

    if (chip.type === 'bookmaker') {
      const current = filters?.selectedBookmakers;
      if (!Array.isArray(current)) {
        return;
      }
      setSelectedBookmakers(current.filter((id) => id !== chip.item));
      return;
    }

    if (chip.type === 'championships-none' || chip.type === 'championships-summary') {
      setSelectedChampionships(null);
      return;
    }

    if (chip.type === 'championship') {
      const current = filters?.selectedChampionships;
      if (!Array.isArray(current)) {
        return;
      }
      setSelectedChampionships(current.filter((name) => name !== chip.item));
      return;
    }

    if (chip.type === 'markets-none' || chip.type === 'markets-summary') {
      setSelectedMarketKeys(null);
      return;
    }

    if (chip.type === 'market') {
      const current = filters?.selectedMarketKeys;
      if (!Array.isArray(current)) {
        return;
      }
      setSelectedMarketKeys(current.filter((key) => key !== chip.item));
    }
  }, [
    filters?.selectedBookmakers,
    filters?.selectedChampionships,
    filters?.selectedMarketKeys,
    setMinSpread,
    setMaxOdds,
    setSearchQuery,
    setTimeWindow,
    setSelectedBookmakers,
    setSelectedChampionships,
    setSelectedMarketKeys
  ]);

  const handleClearAll = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  if (!hasActiveFilters || chips.length === 0) {
    return null;
  }

  return (
    <div
      className={`best-odds-active-chips ${className}`.trim()}
      role="list"
      aria-label={t('bestOdds.filters.activeFilters')}
    >
      <span className="best-odds-active-chips__heading">
        {t('bestOdds.filters.activeFilters')}
      </span>

      <div className="best-odds-active-chips__row">
        {chips.map((chip) => (
          <span key={chip.id} className="best-odds-active-chips__chip" role="listitem">
            <span className="best-odds-active-chips__chip-label" title={chip.label}>
              {chip.label}
            </span>
            <button
              type="button"
              className="best-odds-active-chips__chip-remove"
              onClick={() => handleRemoveChip(chip)}
              aria-label={t('bestOdds.filters.removeFilterAriaLabel', { name: chip.label })}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <button
        type="button"
        className="best-odds-active-chips__clear-all"
        onClick={handleClearAll}
        aria-label={t('bestOdds.filters.clearAllAriaLabel')}
      >
        {t('bestOdds.filters.clearAll')}
      </button>
    </div>
  );
};

BestOddsActiveFilterChips.propTypes = {
  className: PropTypes.string
};

export default BestOddsActiveFilterChips;
